import { buildSupportReply } from '../../core/help/supportAssistant';
import type { SupportAssistantAction, SupportHistoryItem } from '../../core/help/supportAssistant';
import { isAppError, normalizeError } from '../../core/errors/index';
import { withRetry } from '../../core/utils/retry';
import { DATA_ADAPTER } from '../../config/adapters';
import { supportChat } from '../rag/RagService';
import { readRemoteConfigFlags } from '../firebase/remoteConfigFlags';
import { getTraceContext } from '../audit/traceContext';
import type { Language } from '../../types';

export type AssistReplySource = 'cloud' | 'local' | 'degraded';

export interface AssistUserParams {
  message: string;
  houseId?: string;
  role?: string;
  lang?: Language;
  history?: SupportHistoryItem[];
  /** When false, skip cloud assist and use local engine only. */
  networkOnline?: boolean;
  traceId?: string;
}

export interface AssistUserResult {
  reply: string;
  confidence: number;
  escalate: boolean;
  category: string;
  priority: string;
  source: AssistReplySource;
  intent?: string;
  actions?: SupportAssistantAction[];
  handoffOffer?: boolean;
  followUp?: string;
  diagnosticSteps?: string[];
}

const ASSIST_SOFT_TIMEOUT_MS = 12_000;
const ASSIST_RETRY_ATTEMPTS = 2;

function withSoftTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('assist soft timeout'));
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

function isHardAssistError(error: unknown): boolean {
  const appErr = isAppError(error) ? error : normalizeError(error);
  if (appErr.retryable) return false;
  if (appErr.category === 'offline' || appErr.category === 'unavailable') return false;
  return true;
}

function buildLocalResult(
  params: Pick<AssistUserParams, 'message' | 'role' | 'lang' | 'history'>,
  source: AssistReplySource,
): AssistUserResult {
  const { message, role, lang = 'es', history = [] } = params;
  const local = buildSupportReply({ message, role, lang, history });
  return { ...local, source };
}

export async function assistUser(params: AssistUserParams): Promise<AssistUserResult> {
  const {
    message,
    houseId,
    role,
    lang = 'es',
    history = [],
    networkOnline = true,
    traceId,
  } = params;

  const localParams = { message, role, lang, history };

  if (!networkOnline) {
    return buildLocalResult(localParams, 'local');
  }

  if (DATA_ADAPTER !== 'firebase') {
    return buildLocalResult(localParams, 'local');
  }

  const flags = readRemoteConfigFlags();
  if (!flags.aiAssist) {
    return buildLocalResult(localParams, 'local');
  }

  const ctxTraceId = traceId ?? getTraceContext().traceId;

  try {
    const cloud = await withRetry(
      () => withSoftTimeout(
        supportChat({
          message,
          houseId,
          history,
          lang,
          role,
          traceId: ctxTraceId,
        }),
        ASSIST_SOFT_TIMEOUT_MS,
      ),
      { attempts: ASSIST_RETRY_ATTEMPTS, baseDelayMs: 600 },
    );

    if (cloud?.reply?.trim()) {
      return {
        reply: cloud.reply,
        confidence: cloud.confidence ?? 0.7,
        escalate: Boolean(cloud.escalate),
        category: cloud.category ?? 'other',
        priority: cloud.priority ?? 'normal',
        intent: cloud.intent,
        actions: cloud.actions,
        handoffOffer: cloud.handoffOffer,
        followUp: cloud.followUp,
        diagnosticSteps: cloud.diagnosticSteps,
        source: 'cloud',
      };
    }
  } catch (err) {
    if (isHardAssistError(err)) throw err;
    console.warn('[assistUser] Cloud support chat degraded:', err);
  }

  return buildLocalResult(localParams, 'degraded');
}
